import { z } from 'zod';
import { Checklist } from './api.ts';
import { CustomerViewSettings } from './customer-view.ts';
import { SiteConfig } from './installer.ts';
import { SiteProfile } from './site-profile.ts';

// Maintenance in the installer area (ADR-0021)

/** transfer file between Pis: contains device passwords, only for STE staff */
export const SiteBackup = z.object({
  format: z.literal('ste-site-backup'),
  version: z.literal(1),
  createdAt: z.string(),
  serial: z.string().optional(),
  gatewayVersion: z.string().optional(),
  siteTitle: z.string().optional(),
  /** without sponsor token (bound to the hardware) */
  config: SiteConfig,
  profile: SiteProfile.optional(),
  customerView: CustomerViewSettings.optional(),
  checklist: Checklist.optional(),
});
export type SiteBackup = z.infer<typeof SiteBackup>;

export const RestartRequest = z.object({ target: z.enum(['evcc', 'gateway']) });
export type RestartRequest = z.infer<typeof RestartRequest>;

/** the serial number (or "ZURÜCKSETZEN" without one) typed in as second confirmation */
export const FactoryResetRequest = z.object({ confirm: z.string().min(1).max(64) });
export type FactoryResetRequest = z.infer<typeof FactoryResetRequest>;

export const MaintenanceInfo = z.object({
  serial: z.string().optional(),
  gatewayVersion: z.string().optional(),
  evccVersion: z.string().optional(),
  /** text to type for the factory reset */
  resetConfirmation: z.string(),
  /** the viewer may download/restore backups (STE staff) */
  staff: z.boolean(),
  admin: z.boolean(),
  /** "Gateway neu starten" is available (not in development without a supervisor) */
  gatewayRestart: z.boolean(),
});
export type MaintenanceInfo = z.infer<typeof MaintenanceInfo>;

// ---- update channel and pause per site (ADR-0022) ---------------------------------------

export const UpdateChannel = z.enum(['stable', 'beta']);
export type UpdateChannel = z.infer<typeof UpdateChannel>;

export const UpdatePolicy = z.object({
  channel: UpdateChannel.default('stable'),
  /** yyyy-mm-dd: no automatic updates up to and including this day */
  pausedUntil: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .default(null),
});
export type UpdatePolicy = z.infer<typeof UpdatePolicy>;

export const UpdatePolicyInfo = UpdatePolicy.extend({
  /** STE staff in STE Connect */
  editable: z.boolean(),
});
export type UpdatePolicyInfo = z.infer<typeof UpdatePolicyInfo>;

/** result of the last installation by ste-update on the Pi (review 2026-10-15 no. 9 and 10) */
export const UpdateStatus = z.object({
  /** rolled_back: not tried again automatically, only with `ste update --force` */
  result: z.enum(['ok', 'rolled_back', 'error']),
  version: z.string().max(32),
  from: z.string().max(32),
  at: z.string().max(40),
  message: z.string().max(200).optional(),
});
export type UpdateStatus = z.infer<typeof UpdateStatus>;

// ---- change log for customers (ADR-0024) -------------------------------------------------

/** actions shown to customers; anything else (tests, syncs, logins) stays in the technical audit */
export const CHANGE_ACTIONS = [
  'config.apply',
  'loadpoint.mode',
  'loadpoint.limitSoc',
  'loadpoint.plan',
  'loadpoint.plan.delete',
  'loadpoint.vehicle',
  'loadpoint.minTemperature',
  'battery.settings',
  'installer.siteProfile',
  'installer.customerView',
  'installer.checklist',
  'installer.checklist.sign',
  'installer.checklist.share',
  'installer.checklist.reopen',
  'sponsor.set',
  'sponsor.remove',
  'network.wifi.connect',
  'network.wifi.forget',
  'network.saved.forget',
  'network.saved.order',
  'network.setup.open',
  'network.setup.close',
  'maintenance.restart',
  'maintenance.backup',
  'maintenance.restore',
  'maintenance.evccDb',
  'maintenance.log',
  'maintenance.factoryReset',
  'auth.serviceAccess',
  'auth.recovery',
] as const;

/** details that may be shown to customers; never keys, passwords or tokens */
export const CHANGE_DETAIL_KEYS = [
  'comment',
  'mode',
  'soc',
  'temperature',
  'vehicle',
  'ssid',
  'name',
  'role',
  'prioritySoc',
  'bufferSoc',
  'dischargeControl',
  'shared',
  'from',
] as const;

export const ChangeEntry = z.object({
  id: z.number(),
  timestamp: z.string(),
  email: z.string(),
  role: z.string(),
  /** local: on site, cloud: through STE Connect */
  source: z.enum(['local', 'cloud', 'system']),
  action: z.string(),
  target: z.string().nullable(),
  details: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
  ok: z.boolean(),
});
export type ChangeEntry = z.infer<typeof ChangeEntry>;
